"use client";

import { ArrowDown, Check, HeartPulse, Scissors, Stethoscope, Sparkles, Siren } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function DfwCanopyCare() {
  const {
    ref: headerRef,
    isVisible: headerVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: contentRef,
    isVisible: contentVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const tabs = [{
    value: "wellness",
    label: "Tree Health",
    icon: HeartPulse,
    title: "01 // Tree Health & Vitality",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arbor_pro_health_check.png",
    imageAlt: "Arborist inspecting the canopy of a mature oak tree in Fort Worth",
    description1: "Preserving the structural integrity and biological health of your trees is our primary commitment. Our diagnostic approach identifies nutrient deficiencies and pest threats before they compromise your landscape.",
    description2: "From deep-root fertilization to systemic injections, we utilize surgical precision to ensure your canopy thrives in the demanding North Texas climate, season after season.",
    services: ["Soil & Root Analysis", "Deep Root Fertilization", "Pest & Disease Control", "Growth Regulation", "Drought Stress Recovery", "Oak Wilt Management"],
    stats: [{
      label: "Inspection Time",
      value: "45–60 min"
    }, {
      label: "Trees Saved Yearly",
      value: "1,200+"
    }, {
      label: "Expert Support",
      value: "Certified"
    }]
  }, {
    value: "surgical",
    label: "Precision Pruning",
    icon: Scissors,
    title: "02 // Structural Pruning",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arbor_pro_precision_pruning.png",
    imageAlt: "Professional arborist performing structural pruning on a residential tree",
    description1: "Our pruning methodology follows strict ISA standards to improve tree structure, enhance aesthetics, and mitigate risk. We focus on the 'Detail of the Cut' to promote rapid healing and long-term stability.",
    description2: "Whether it's canopy thinning for wind resistance or crown raising for clearance, our team operates with technical mastery to protect your property and the tree's natural form.",
    services: ["Structural Pruning", "Deadwood Removal", "Canopy Thinning", "Crown Reduction", "Vista Pruning", "Young Tree Training"],
    stats: [{
      label: "Typical Duration",
      value: "2–6 hours"
    }, {
      label: "Projects Monthly",
      value: "85+"
    }, {
      label: "Safety Rating",
      value: "100% Compliant"
    }]
  }, {
    value: "diagnostics",
    label: "Risk Assessment",
    icon: Stethoscope,
    title: "03 // Risk & Hazard Analysis",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arbor_pro_risk_assessment.png",
    imageAlt: "Arborist using technical equipment to assess tree stability",
    description1: "When a tree's stability is in question, accurate data is essential. We provide comprehensive hazard evaluations to identify internal decay and structural weaknesses that aren't visible to the untrained eye.",
    description2: "Our technical reports give DFW homeowners clarity and peace of mind, offering actionable solutions ranging from cabling and bracing to strategic removal when necessary.",
    services: ["Level 2 Risk Assessments", "Decay Detection", "Cabling & Bracing", "Storm Damage Audits", "Construction Protection", "Insurance Documentation"],
    stats: [{
      label: "Report Delivery",
      value: "24–48 hours"
    }, {
      label: "Assessments Monthly",
      value: "210+"
    }, {
      label: "Precision Level",
      value: "Surgical"
    }]
  }, {
    value: "dental",
    label: "Safe Removal",
    icon: Sparkles,
    title: "04 // Technical Removal",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arbor_pro_tree_removal.png",
    imageAlt: "Controlled tree removal in a tight residential space",
    description1: "Tree removal is a high-stakes operation that requires absolute control. We specialize in technical removals in confined residential spaces, utilizing advanced rigging to protect your home and landscape.",
    description2: "Our process is clean, efficient, and thorough. From the first cut to the final stump grinding, we treat your property with the respect it deserves, leaving no trace behind but a safer environment.",
    services: ["Complex Tree Removal", "Crane-Assisted Removal", "Stump Grinding", "Debris Hauling", "Site Restoration", "Emergency Extraction"],
    stats: [{
      label: "Setup Time",
      value: "30–90 min"
    }, {
      label: "Removals Monthly",
      value: "140+"
    }, {
      label: "Cleanup Standard",
      value: "Pristine"
    }]
  }, {
    value: "emergency",
    label: "Storm Response",
    icon: Siren,
    title: "05 // 24/7 Storm Response",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1697657/img/arbor_pro_emergency_storm.png",
    imageAlt: "Arbor Pro emergency crew responding to storm damage at night",
    description1: "When Texas storms strike, our rapid-response teams are ready. We provide immediate triage for fallen trees and hazardous limbs, stabilizing your property and preventing further damage.",
    description2: "We coordinate directly with homeowners and insurance providers during high-stress moments, offering the calm, professional expertise needed to restore safety to your DFW residence.",
    services: ["24/7 Emergency Triage", "Hazardous Limb Removal", "Power Line Clearance", "Structural Stabilization", "Storm Debris Clearing", "Insurance Claim Support"],
    stats: [{
      label: "Response Time",
      value: "Under 2 hours"
    }, {
      label: "Storms Managed",
      value: "50+ Yearly"
    }, {
      label: "Availability",
      value: "24/7/365"
    }]
  }];
  return <section data-section-id="915" id="dfw-canopy-care" className="bg-background text-foreground pt-24 pb-20 md:pt-[120px] md:pb-[100px]">
      <div className="container mx-auto px-6 lg:px-8">
        <div ref={headerRef} className={`mx-auto max-w-3xl text-center transition-all duration-700 ease-out ${headerVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <span className="text-xs font-mono font-medium tracking-[0.2em] uppercase text-secondary">
            Professional Tree Care Specialists
          </span>
          <h2 className="font-default mt-5 text-4xl sm:text-5xl md:text-[60px] md:leading-[0.95] tracking-tighter font-extrabold text-foreground">
            Surgical Precision for the DFW Landscape.
          </h2>
          <p className="mt-6 text-lg text-secondary font-default leading-relaxed">
            Premium residential tree care delivered with technical mastery. From structural pruning to emergency response, we protect the value and safety of your home.
          </p>
        </div>

        <div ref={contentRef} className={`mt-14 md:mt-20 transition-all duration-700 delay-150 ease-out ${contentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
          <Tabs defaultValue="wellness" className="w-full">
            <div className="flex justify-center">
              <TabsList className="bg-transparent h-auto flex flex-wrap justify-center gap-2 p-0">
                {tabs.map((tab, i) => {
                const Icon = tab.icon;
                return <TabsTrigger key={tab.value} value={tab.value} data-index={i} className="rounded-md border border-border bg-card text-primary hover:bg-accent data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:border-primary px-5 py-2.5 h-auto text-sm font-mono font-medium transition-colors">
                      <Icon className="size-4" />
                      <span>{tab.label}</span>
                    </TabsTrigger>;
              })}
              </TabsList>
            </div>

            {tabs.map((tab, i) => <TabsContent key={tab.value} value={tab.value} data-index={i} className="mt-12 md:mt-16 data-[state=active]:animate-in data-[state=active]:fade-in-50 data-[state=active]:duration-500">
                <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-start">
                  <div className="lg:col-span-2 relative">
                    <div className="absolute -top-6 -left-6 w-40 h-40 md:w-56 md:h-56 rounded-md bg-accent/10 blur-2xl" aria-hidden="true" />
                    <div className="absolute -bottom-8 -right-4 w-32 h-32 md:w-44 md:h-44 rounded-md bg-primary/5 blur-2xl" aria-hidden="true" />
                    <div className="relative overflow-hidden rounded-md shadow-lg border border-border/50">
                      <img src={tab.image} data-wvc-srcset={`${tab.image}-wvc-srcset`} data-wvc-sizes={`${tab.image}-wvc-sizes`} alt={tab.imageAlt} loading="lazy" decoding="async" className="w-full h-[420px] md:h-[520px] object-cover grayscale hover:grayscale-0 transition-all duration-500" />
                    </div>
                    <div className="absolute -bottom-6 -left-2 md:-left-6 size-20 md:size-24 rounded-md bg-accent text-accent-foreground shadow-md flex items-center justify-center rotate-[-3deg] border border-primary/10">
                      <tab.icon className="size-9 md:size-10" />
                    </div>
                  </div>

                  <div className="lg:col-span-3">
                    <h3 className="font-default font-bold text-3xl md:text-4xl lg:text-[40px] text-foreground tracking-tight">
                      {tab.title}
                    </h3>
                    <div className="mt-5 space-y-4 text-[17px] leading-relaxed text-foreground/80 font-default">
                      <p>{tab.description1}</p>
                      <p>{tab.description2}</p>
                    </div>

                    <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                      {tab.services.map((service, j) => <div key={service} data-index={j} className="flex items-center gap-3 rounded-md bg-card px-4 py-3 border border-border/40 hover:border-accent/50 transition-colors">
                          <span className="flex-shrink-0 size-5 rounded-md bg-accent/20 text-primary flex items-center justify-center">
                            <Check className="size-3.5" strokeWidth={3} />
                          </span>
                          <span className="text-sm font-mono font-medium text-foreground">{service}</span>
                        </div>)}
                    </div>

                    <div className="mt-10 rounded-md bg-card border border-border/60 p-5 shadow-sm">
                      <div className="grid grid-cols-3 items-center">
                        {tab.stats.map((stat, k) => <div key={stat.label} data-index={k} className={`flex flex-col items-center text-center px-2 ${k < tab.stats.length - 1 ? "border-r border-border/60" : ""}`}>
                            <span className="text-[10px] md:text-xs font-mono uppercase tracking-[0.15em] text-secondary">
                              {stat.label}
                            </span>
                            <span className="mt-2 font-default font-bold text-base md:text-xl text-foreground">
                              {stat.value}
                            </span>
                          </div>)}
                      </div>
                    </div>

                    <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-5 sm:gap-8">
                      <Button asChild size="lg" className="bg-accent text-accent-foreground hover:bg-accent/90 rounded-md px-7 h-12 text-sm font-mono font-bold shadow-md border border-primary/10">
                        <Link to="/GetQuotePage">Request Service Quote</Link>
                      </Button>
                      <Link to="/OurExpertisePage" className="inline-flex items-center gap-2 text-sm font-mono font-medium text-primary hover:text-accent transition-colors group">
                        <span>View technical standards</span>
                        <ArrowDown className="size-4 transition-transform group-hover:translate-y-0.5" />
                      </Link>
                    </div>
                  </div>
                </div>
              </TabsContent>)}
          </Tabs>

          <Separator className="mt-20 bg-border/40" />
        </div>
      </div>
    </section>;
}